<template>
    <div class="accordion">
        <template
            v-for="(item, key) in items"
            :key="key"
        >
            <section
                v-if="show(item)"
                class="accordion-section"
                :class="getClass(key)"
            >
                <button
                    :id="buttonId(key)"
                    class="title"
                    :aria-expanded="!!expandedComp[key]"
                    :aria-controls="panelId(key)"
                    type="button"
                    @click="toggle(key)"
                >
                    {{ getTitle(item) }}
                </button>

                <div
                    :id="panelId(key)"
                    class="panel"
                    role="region"
                    :aria-labelledby="buttonId(key)"
                >
                    <div class="panel-inner">
                        <div
                            v-if="
                                item.__typename ===
                                    'ProductPageDetailsAccordionItemGeneric' ||
                                item.__typename ===
                                    'ProductPageDetailsAccordionItemFinance' ||
                                item.__typename ===
                                    'ProductPageDetailsAccordionItemShipping' ||
                                item.__typename ===
                                    'ProductPageDetailsAccordionItemSizing' ||
                                item.__typename ===
                                    'ProductPageDetailsAccordionItemWarranty'
                            "
                        >
                            <CmsComponent
                                v-for="(
                                    component, componentKey
                                ) in item.components"
                                :key="componentKey"
                                :background="'WHITE'"
                                :data="component"
                                :product="product"
                                :related-products="relatedProducts"
                            />
                        </div>
                        <div
                            v-else-if="
                                item.__typename ===
                                'ProductPageDetailsAccordionItemAboutThisProduct'
                            "
                        >
                            <slot name="aboutThisProduct"></slot>
                        </div>
                        <div
                            v-else-if="
                                item.__typename ===
                                'ProductPageDetailsAccordionItemDesignInspiration'
                            "
                        >
                            <slot name="designInspiration"></slot>
                        </div>
                    </div>
                </div>
            </section>
        </template>
    </div>
</template>

<script setup lang="ts">
import type {
    PdpAccordionItemFragment,
    ShopWithConfidenceProductFragment,
} from '@/graphql/api'
import CmsComponent from '@/components/cms/CmsComponent.vue'
import type { Props as RelatedProductProps } from '@/components/rings/RelatedProducts.vue'

const {
    items,
    aboutThisEntityLabel,
    resizeable = false,
} = defineProps<{
    items: PdpAccordionItemFragment[]
    aboutThisEntityLabel: string
    resizeable?: boolean
    relatedProducts: RelatedProductProps | undefined
    product: ShopWithConfidenceProductFragment | undefined
}>()

import { assertNever } from '@/helpers'
import { computed, ref } from 'vue'
import { useSegment } from '@/third-party/segment.ts'
import { useCurrentUrl } from '@/plugins/current-url.ts'

const getTitle = (item: PdpAccordionItemFragment): string => {
    switch (item.__typename) {
        case 'ProductPageDetailsAccordionItemGeneric':
            return item.title
        case 'ProductPageDetailsAccordionItemAboutThisProduct':
            return `About this ${aboutThisEntityLabel}`
        case 'ProductPageDetailsAccordionItemShipping':
            return 'Insured & Free Shipping'
        case 'ProductPageDetailsAccordionItemSizing':
            return 'Sizing'
        case 'ProductPageDetailsAccordionItemWarranty':
            return 'Lifetime warranty'
        case 'ProductPageDetailsAccordionItemFinance':
            return 'Interest Free Financing'
        case 'ProductPageDetailsAccordionItemDesignInspiration':
            return 'Design inspiration'
        default:
            return assertNever(item, 'Item')
    }
}

const expanded = ref<boolean[]>([])

const expandedComp = computed<boolean[], number>({
    get: () =>
        items.map((item, key) =>
            key in expanded.value ? expanded.value[key]! : item.expanded
        ),
    set: (newValue) => {
        expanded.value[newValue] = !expandedComp.value[newValue]
    },
})

const { track } = useSegment()

const currentUrl = useCurrentUrl()

const toggle = (index: number): void => {
    expandedComp.value = index

    const url = currentUrl.value.toString()

    track('custom_pdp_accordion_click', {
        param1: 'Information Accordion',
        param2: getTitle(items[index]!),
        param3: expanded.value[index] ? 'open' : 'close',
        param4: url,
        url,
        event_name: 'pdp_accordion_click',
    })
}
const getClass = (index: number) => {
    return { 'active': expandedComp.value[index] }
}

const slots = defineSlots<{
    aboutThisProduct(): any
    designInspiration(): any
}>()

const show = (item: PdpAccordionItemFragment): boolean => {
    return (
        (item.__typename !==
            'ProductPageDetailsAccordionItemAboutThisProduct' ||
            slots['aboutThisProduct']) &&
        (item.__typename !==
            'ProductPageDetailsAccordionItemDesignInspiration' ||
            slots['designInspiration']) &&
        (item.__typename !== 'ProductPageDetailsAccordionItemSizing' ||
            resizeable)
    )
}

const panelId = (i: number): string => `pdp-accordion-panel-${i}`
const buttonId = (i: number): string => `pdp-accordion-button-${i}`
</script>

<style scoped lang="scss">
.accordion {
    margin: 60px 0 0;
    border-bottom: 1px solid $grey-300;
}

.accordion-section {
    position: relative;

    .title {
        border-top: 1px solid $grey-300;
        width: 100%;
        height: 50px;
        padding: 10px 10px 10px 0;
        line-height: 30px;
        align-items: center;
        justify-content: space-between;
        cursor: pointer;
        font-family: $parcel-font-stack;
        color: $blue;
        display: flex;
        font-size: 1.125rem;
        text-transform: capitalize;

        &:focus-visible {
            background: rgba($blue, 0.1);
            border-color: transparent;
            outline: 1px solid $grey-300;
        }

        &::after {
            font-family: $icon-font;
            content: $icon-angle-down;
            width: 30px;
            height: 30px;
            text-align: center;
            display: block;
        }
    }

    .panel {
        display: grid;
        grid-template-rows: 0fr;
        overflow: hidden;
        transition: grid-template-rows 200ms
            var(--timing-function-easing-emphasized);
        padding: 0;

        .panel-inner {
            min-height: 0;
            overflow: hidden;
            opacity: 0;
            transition: opacity 150ms var(--timing-function-easing-emphasized);

            :deep(.content) {
                padding: 0;
            }
        }
    }

    &.active {
        .title {
            &::after {
                content: $icon-angle-up;
            }
        }

        .panel {
            grid-template-rows: 1fr;

            .panel-inner {
                opacity: 1;
                padding: 0 0 10px 0;
            }
        }
    }
}
</style>
